/**
 * KpiBar v2 (estilo Shopify) — 100% Polaris-first. Indicador con delta + sparkline,
 * para comparar contra el v1. Layout SOLO con primitivos Polaris (InlineGrid / Box /
 * InlineStack / Text), sin divs ni estilos inline para layout:
 *   - Reparto proporcional: InlineGrid con columnas `auto` (periodo/carrusel) + `1fr`
 *     por métrica → las métricas crecen parejo y llenan el ancho (sin minWidth fijo).
 *   - Por celda: InlineGrid `1fr auto` (valor a la izquierda, sparkline a la derecha).
 *   - Delta period-over-period (flecha + % con color) por métrica.
 *   - Sparkline real con @shopify/polaris-viz.
 *   - Selector de periodo (Button + Popover) opcional.
 *
 * Componente genérico: el periodo es un string opaco; el padre define las opciones.
 */
import { useState, useRef, useEffect } from 'react';
import {
  Box,
  BlockStack,
  Text,
  InlineStack,
  InlineGrid,
  Button,
  Popover,
  RadioButton,
  Spinner,
} from '@/lib/polaris';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from '@/lib/polaris-icons';
import { SparkLineChart, PolarisVizProvider } from '@shopify/polaris-viz';
import '@shopify/polaris-viz/build/esm/styles.css';
import './KpiBarV2.css';

/** Props de celda (padding + divisor derecho). Solo tokens Polaris. */
const CELL = {
  paddingBlock: '400' as const,
  paddingInline: '500' as const,
  borderInlineEndWidth: '025' as const,
  borderColor: 'border-secondary' as const,
} as const;

/**
 * Theme polaris-viz: fondo TRANSPARENTE (sin la caja blanca fea detrás del sparkline,
 * para que se funda con la celda/pastilla como en Shopify) + línea más gruesa (3px).
 * GOTCHA: el tema por defecto de polaris-viz se llama 'Light' (DEFAULT_THEME_NAME), NO
 * 'Default'. Las sparklines (sin prop `theme`) usan 'Light'; el cuadro grande usa 'Default'.
 * Override de ambos para que el fondo transparente aplique en todos lados.
 */
const VIZ_CHART = { chartContainer: { backgroundColor: 'transparent' } };
const VIZ_LINE = { line: { hasArea: true, hasSpline: true, width: 3, pointStroke: 'transparent' } };
const VIZ_THEMES = {
  Light: { ...VIZ_CHART, ...VIZ_LINE },
  Default: { ...VIZ_CHART, ...VIZ_LINE },
};

export interface KpiV2Item {
  key: string;
  label: string;
  /** Valor formateado para mostrar (p. ej. "S/ 349.00"). */
  value: string;
  /** Numérico actual y del periodo anterior, para el delta. `previous` undefined =
   *  métrica sin comparación (no de flujo / sin periodo anterior) → no se muestra delta. */
  raw: number;
  previous?: number;
  /** Serie del periodo actual (buckets) para el sparkline. Vacía = sin sparkline. */
  series: number[];
  /** Si "bajar" es bueno (p. ej. saldo por cobrar) invierte el color del delta. */
  invertDelta?: boolean;
}

export interface KpiBarV2Props {
  items: KpiV2Item[];
  loading?: boolean;
  /** Selector de periodo (opcional). Si no se pasan opciones, no se muestra la celda
   *  — útil cuando el periodo lo maneja otro control (p. ej. el rango de fechas de Inicio). */
  period?: string;
  periodOptions?: { value: string; label: string }[];
  onPeriodChange?: (value: string) => void;
  /** Celdas a la izquierda (cada item = una celda `auto` con su divisor), en vez del periodo. */
  leftContent?: React.ReactNode[];
  visibleCount?: number;
  /** Si se define, las celdas son clicables (seleccionan el indicador para el cuadro de detalle). */
  selectedKey?: string;
  onSelectMetric?: (key: string) => void;
  /** Fila inferior integrada al mismo cuadro (estilo Shopify): el detalle del indicador
   *  seleccionado va debajo de la fila de KPIs, dentro del mismo borde/superficie. */
  detail?: React.ReactNode;
}

function computeDelta(raw: number, previous: number): { pct: number | null; dir: 'up' | 'down' | 'flat' } {
  // Sin base de comparación: si ahora hay valor (raw>0) marcamos dirección "up" (creció
  // desde cero); si ambos son 0, "flat" → "—". No hay % honesto al dividir entre 0.
  if (previous === 0) return { pct: null, dir: raw > 0 ? 'up' : 'flat' };
  const pct = ((raw - previous) / previous) * 100;
  const dir = pct > 0.5 ? 'up' : pct < -0.5 ? 'down' : 'flat';
  return { pct, dir };
}

function DeltaBadge({ raw, previous, invert }: { raw: number; previous: number; invert?: boolean }) {
  const { pct, dir } = computeDelta(raw, previous);
  if (pct === null) {
    // Creció desde cero (sin periodo anterior para % exacto): flecha de dirección.
    if (dir === 'up') {
      const tone = invert ? 'critical' : 'success';
      return <Text variant="bodySm" tone={tone} fontWeight="medium" as="span">↗</Text>;
    }
    return <Text variant="bodySm" tone="subdued" as="span">—</Text>;
  }
  const good = invert ? dir === 'down' : dir === 'up';
  const tone = dir === 'flat' ? 'subdued' : good ? 'success' : 'critical';
  const arrow = dir === 'up' ? '↗' : dir === 'down' ? '↘' : '→';
  return (
    <Text variant="bodySm" tone={tone} fontWeight="medium" as="span">{`${arrow} ${Math.abs(pct).toFixed(0)}%`}</Text>
  );
}

function PeriodSelector({
  period,
  options,
  onChange,
}: {
  period: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === period)?.label ?? '—';
  return (
    <Box paddingBlock="300" paddingInline="300" borderInlineEndWidth="025" borderColor="border-secondary">
      <Popover
        active={open}
        autofocusTarget="first-node"
        preferredAlignment="left"
        preferredPosition="below"
        onClose={() => setOpen(false)}
        activator={
          <Button variant="tertiary" icon={CalendarIcon} onClick={() => setOpen((o) => !o)}>
            {current}
          </Button>
        }
      >
        <Box padding="300" minWidth="220px">
          <BlockStack gap="200">
            {options.map((opt) => (
              <RadioButton
                key={opt.value}
                label={opt.label}
                checked={period === opt.value}
                id={`kpi-v2-period-${opt.value}`}
                name="kpi-v2-period"
                onChange={() => { onChange(opt.value); setOpen(false); }}
              />
            ))}
          </BlockStack>
        </Box>
      </Popover>
    </Box>
  );
}

function KpiCell({ item, selected, onSelect }: { item: KpiV2Item; selected?: boolean; onSelect?: () => void }) {
  const hasSeries = item.series.length > 0;
  const chartData = [{ data: item.series.map((v, i) => ({ key: i, value: v })) }];
  const labelBlock = (
    <BlockStack gap="050">
      <Text variant="bodySm" tone="subdued" fontWeight="medium" truncate as="span">{item.label}</Text>
      <InlineStack gap="150" blockAlign="center" wrap={false}>
        <Text variant="headingMd" truncate as="span">{item.value}</Text>
        {item.previous !== undefined && (
          <DeltaBadge raw={item.raw} previous={item.previous} invert={item.invertDelta} />
        )}
      </InlineStack>
    </BlockStack>
  );
  // Si no hay serie temporal (p. ej. métricas all-time de Clientes), sin sparkline.
  const inner = hasSeries ? (
    <InlineGrid columns="1fr auto" gap="400" alignItems="center">
      {labelBlock}
      <Box width="80px" minHeight="24px">
        <SparkLineChart isAnimated data={chartData} accessibilityLabel={`Tendencia de ${item.label}`} />
      </Box>
    </InlineGrid>
  ) : labelBlock;
  // Celda no seleccionable (loading / Operaciones): solo divisor + padding.
  if (!onSelect) return <Box {...CELL}>{inner}</Box>;
  // Seleccionable: divisor en el borde de la celda + "pastilla" redondeada con el MISMO
  // hover que los botones terciarios (Fecha/Canal). El padding="100" deja el inset para
  // que la pastilla no toque los bordes. Seleccionada = la pastilla queda con fondo.
  return (
    <Box borderInlineEndWidth="025" borderColor="border-secondary" padding="100">
      <button type="button" className="KpiBarV2-metric" aria-pressed={selected} onClick={onSelect}>
        {inner}
      </button>
    </Box>
  );
}

export function KpiBarV2({
  items,
  loading = false,
  period,
  periodOptions,
  onPeriodChange,
  leftContent,
  visibleCount = 4,
  selectedKey,
  onSelectMetric,
  detail,
}: KpiBarV2Props) {
  const showPeriod = !!periodOptions && periodOptions.length > 0 && !!onPeriodChange && period !== undefined;
  const [carouselStart, setCarouselStart] = useState(0);

  // Conteo de indicadores RESPONSIVE (estilo Shopify): en pantallas anchas mostramos MÁS
  // indicadores en vez de estirar 3. Medimos el ancho real del contenedor y calculamos cuántos
  // entran a un ancho cómodo por indicador. Así, en fullWidth, 3 → 4 → 5 según el monitor.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setContainerWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const TARGET_CELL = 250; // ancho cómodo por indicador (px) → cada ~250px de ancho = 1 indicador más
  const FIXED_COLS = (leftContent?.length ?? 0) * 130 + (showPeriod ? 150 : 0) + 64; // periodo/left + flechas
  const fit = containerWidth > 0 ? Math.floor((containerWidth - FIXED_COLS) / TARGET_CELL) : visibleCount;
  const effectiveVisible = Math.max(3, Math.min(fit, items.length));

  const maxStart = Math.max(0, items.length - effectiveVisible);
  const clampedStart = Math.min(carouselStart, maxStart);
  const visibleItems = items.slice(clampedStart, clampedStart + effectiveVisible);
  const showCarousel = items.length > effectiveVisible;

  // Plantilla de columnas: `auto` para periodo/carrusel (compactos) y
  // `minmax(max-content, 1fr)` por métrica → la celda NUNCA baja de su contenido
  // natural (no aprieta el valor contra el sparkline) y, si sobra espacio, crece
  // parejo para llenar el ancho (sin hueco muerto a la derecha).
  const METRIC_COL = 'minmax(max-content, 1fr)';
  const tracks: string[] = [];
  (leftContent ?? []).forEach(() => tracks.push('auto'));
  if (showPeriod) tracks.push('auto');
  if (loading) tracks.push(METRIC_COL);
  else visibleItems.forEach(() => tracks.push(METRIC_COL));
  if (showCarousel) tracks.push('auto');
  const columns = tracks.join(' ');

  return (
    <PolarisVizProvider themes={VIZ_THEMES}>
      <Box ref={containerRef} background="bg-surface" borderRadius="300" borderWidth="025" borderColor="border" overflowX="hidden" overflowY="hidden">
        <InlineGrid columns={columns} gap="0" alignItems="center">
          {leftContent}
          {showPeriod && (
            <PeriodSelector period={period!} options={periodOptions!} onChange={onPeriodChange!} />
          )}
          {loading ? (
            <Box {...CELL}>
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" />
                <Text as="span" tone="subdued" variant="bodySm">Cargando…</Text>
              </InlineStack>
            </Box>
          ) : (
            visibleItems.map((item) => (
              <KpiCell
                key={item.key}
                item={item}
                selected={item.key === selectedKey}
                onSelect={onSelectMetric ? () => onSelectMetric(item.key) : undefined}
              />
            ))
          )}
          {showCarousel && (
            <Box paddingBlock="200" paddingInline="300" borderInlineStartWidth="025" borderColor="border-secondary">
              <BlockStack gap="050">
                <Button icon={ChevronLeftIcon} variant="secondary" size="slim" accessibilityLabel="Indicadores anteriores" onClick={() => setCarouselStart((i) => Math.max(0, i - 1))} disabled={clampedStart <= 0} />
                <Button icon={ChevronRightIcon} variant="secondary" size="slim" accessibilityLabel="Más indicadores" onClick={() => setCarouselStart((i) => Math.min(maxStart, i + 1))} disabled={clampedStart >= maxStart} />
              </BlockStack>
            </Box>
          )}
        </InlineGrid>
        {detail && (
          <Box borderBlockStartWidth="025" borderColor="border" padding="500">
            {detail}
          </Box>
        )}
      </Box>
    </PolarisVizProvider>
  );
}
